import { getDB } from "./schema.js";
import { followsChanged } from "./cloud-follows.js";
import { nowIso, todayDate } from "../utils/dates.js";
import { getComicSeries, getComicSeriesIssues } from "../api/comics.js";
import { searchBooks } from "../api/books.js";
import { addNotification } from "./notifications.js";
import { dailyBudgetLow } from "../utils/comic-runs.js";
import {
	addDays,
	LOOKBACK_DAYS,
	issueCheckDue,
	tradeCheckDue,
	watchedSeriesIds,
	diffIssues,
	diffTrades,
	subscriptionNotices,
	defaultTradeQuery,
} from "../utils/comic-subscriptions.js";

// Comic series subscriptions (design_documents/Comics/series-subscriptions.md):
// the device-local `series_subscriptions` store (DB v19; backed up, never
// synced) and the release check that turns new issues / trades into bell
// notices. The check runs once a day from main.js's daily maintenance.

const STORE = "series_subscriptions";
// At most this many subscriptions are checked per day (oldest-checked first).
export const CHECKS_PER_PASS = 50;
const LAST_RUN_KEY = "comicSubscriptionsLastRun";

export async function listSubscriptions() {
	const db = await getDB();
	const all = await db.getAll(STORE);
	return all.sort((a, b) => (a.label ?? "").localeCompare(b.label ?? ""));
}

export async function getSubscription(seriesId) {
	const db = await getDB();
	return db.get(STORE, Number(seriesId));
}

async function put(sub) {
	const db = await getDB();
	await db.put(STORE, sub);
	followsChanged();
	return sub;
}

// Subscribe to a Metron series. Fetches the series detail (status + associated
// annuals/specials), then runs a SILENT first check so the current state is
// recorded without notices — only what's new from now on rings the bell.
// `label` is used if the detail can't be fetched (offline).
export async function subscribe(seriesId, { label = null } = {}) {
	const id = Number(seriesId);
	const existing = await getSubscription(id);
	if (existing) return existing;
	let detail = null;
	try {
		detail = await getComicSeries(id);
	} catch {
		// offline / busy — subscribe with what we know; the first check fills in
	}
	const finalLabel = detail?.label ?? label ?? `Series ${id}`;
	let sub = {
		series_id: id,
		label: finalLabel,
		publisher: detail?.publisher ?? null,
		status: detail?.status ?? null,
		associated: (detail?.associated ?? []).map((a) => ({ id: a.series_id, label: a.label, enabled: true })),
		trade_query: defaultTradeQuery(detail?.name ?? finalLabel),
		notify: { announced: true, out: true },
		seen_issue_ids: [],
		out_issue_ids: [],
		seen_trade_keys: [],
		upcoming: [],
		last_checked: null,
		last_trade_check: null,
		created_at: nowIso(),
	};
	await put(sub);
	try {
		sub = (await checkSubscription(sub, { silent: true })).sub;
	} catch {
		// the daily check will catch up
	}
	return sub;
}

export async function unsubscribe(seriesId) {
	const db = await getDB();
	await db.delete(STORE, Number(seriesId));
	followsChanged();
}

// Settings changes: notify toggles, trade_query, associated[].enabled. A new
// trade query restarts the trade watch silently (so its back catalogue isn't
// reported as new).
export async function updateSubscription(seriesId, patch) {
	const current = await getSubscription(seriesId);
	if (!current) return null;
	const next = { ...current, ...patch };
	if ("trade_query" in patch && patch.trade_query !== current.trade_query) {
		next.trade_query = (patch.trade_query ?? "").trim() || null;
		next.seen_trade_keys = [];
		next.last_trade_check = null;
		next.trade_silent = true;
	}
	return put(next);
}

// One subscription's check: issues of every watched series on sale from a
// week ago (one Metron call each), then — weekly — the Open Library trade
// watch. Saves the subscription and posts its notices (unless silent).
// Returns { sub, notices, meta } (meta = the last Metron budget seen).
export async function checkSubscription(sub, { silent = false, today = todayDate(), force = false } = {}) {
	let next = sub;
	let meta = null;
	let announced = [];
	let out = [];
	let trades = [];
	const firstIssueCheck = !sub.last_checked;

	if (force || silent || issueCheckDue(sub, today)) {
		const rows = [];
		for (const id of watchedSeriesIds(sub)) {
			const res = await getComicSeriesIssues(id, { after: addDays(today, -LOOKBACK_DAYS) });
			meta = res.meta;
			rows.push(...res.data.results);
		}
		const d = diffIssues(next, rows, today, { silent: silent || firstIssueCheck });
		next = d.next;
		announced = d.announced;
		out = d.out;
	}

	if (tradeCheckDue(next, today) || (silent && next.trade_query)) {
		try {
			const books = await searchBooks(next.trade_query, { field: "title", sort: "new", limit: 20 });
			const quiet = silent || next.trade_silent || !sub.last_trade_check;
			const d = diffTrades(next, books, today, { silent: quiet });
			next = { ...d.next, trade_silent: false };
			trades = d.trades;
		} catch {
			// Open Library hiccup — try again next pass
		}
	}

	await put(next);
	const notices = silent ? [] : subscriptionNotices(next, { announced, out, trades }, today, nowIso());
	for (const n of notices) await addNotification(n);
	return { sub: next, notices, meta };
}

// The daily run (main.js): at most once a day, oldest-checked first, capped,
// stopping early when Metron's daily budget runs low or Metron is busy.
// Resolves the number of notices posted, or null when it didn't run.
export async function runSubscriptionChecks({ force = false, today = todayDate() } = {}) {
	try {
		if (!force && localStorage.getItem(LAST_RUN_KEY) === today) return null;
	} catch {
		// storage unavailable — run anyway
	}
	if (typeof navigator !== "undefined" && navigator.onLine === false) return null;
	const subs = (await listSubscriptions())
		.filter((s) => force || issueCheckDue(s, today) || tradeCheckDue(s, today))
		.sort((a, b) => (a.last_checked ?? "").localeCompare(b.last_checked ?? ""))
		.slice(0, CHECKS_PER_PASS);
	let posted = 0;
	for (const sub of subs) {
		try {
			const { notices, meta } = await checkSubscription(sub, { today, force });
			posted += notices.length;
			if (dailyBudgetLow(meta)) break;
		} catch (err) {
			console.warn(`Subscription check failed for "${sub.label}":`, err?.message ?? err);
			if (err?.status === 429) break;
		}
	}
	try {
		localStorage.setItem(LAST_RUN_KEY, today);
	} catch {
		// fine — it'll run again next launch
	}
	return posted;
}
